
import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Copy, Eye, EyeOff, Check } from 'lucide-react';
import { toast } from '@/components/ui/use-toast';
import { cn } from '@/lib/utils';

interface CouponCodeButtonProps {
  couponCode: string;
  variant?: 'button' | 'badge';
  size?: 'sm' | 'lg';
  className?: string;
}

const CouponCodeButton = ({ 
  couponCode, 
  variant = 'button', 
  size = 'sm',
  className 
}: CouponCodeButtonProps) => {
  const [isRevealed, setIsRevealed] = useState(false);
  const [isCopied, setIsCopied] = useState(false);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(couponCode);
      setIsCopied(true);
      toast({
        title: "Coupon code copied!",
        description: "The discount code has been copied to your clipboard.",
      });
      setTimeout(() => setIsCopied(false), 2000);
    } catch (error) {
      toast({
        title: "Failed to copy",
        description: "Please copy the code manually.",
        variant: "destructive",
      });
    }
  };

  const handleReveal = () => {
    setIsRevealed(true);
  };

  if (variant === 'badge') {
    return (
      <div className={cn("flex items-center gap-2", className)}>
        <Badge variant="outline" className="font-mono">
          {isRevealed ? couponCode : '••••••••••'}
        </Badge>
        {!isRevealed ? (
          <Button
            size="sm"
            variant="ghost"
            onClick={handleReveal}
            className="h-6 w-6 p-0"
          >
            <Eye className="h-3 w-3" />
          </Button>
        ) : (
          <Button
            size="sm"
            variant="ghost"
            onClick={handleCopy}
            className="h-6 w-6 p-0"
          >
            {isCopied ? (
              <Check className="h-3 w-3 text-green-600" />
            ) : (
              <Copy className="h-3 w-3" />
            )}
          </Button>
        )}
      </div>
    );
  }

  return (
    <div className={cn("flex items-center gap-2", className)}>
      {!isRevealed ? (
        <Button
          size={size}
          variant="outline"
          onClick={handleReveal}
          className="flex items-center gap-2"
        >
          <Eye className="h-4 w-4" />
          Get Code
        </Button>
      ) : (
        <>
          <Badge variant="outline" className="font-mono text-sm px-3 py-1">
            {couponCode}
          </Badge>
          <Button
            size={size}
            variant="ghost"
            onClick={handleCopy}
            className="flex items-center gap-2"
          >
            {isCopied ? (
              <>
                <Check className="h-4 w-4 text-green-600" />
                Copied!
              </>
            ) : (
              <>
                <Copy className="h-4 w-4" />
                Copy
              </>
            )}
          </Button>
        </>
      )}
    </div>
  );
};

export default CouponCodeButton;
